import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Descriptions, Alert } from "antd";
import { CheckCircleFilled, FilePdfOutlined } from "@ant-design/icons";
import SbButton, { BUTTON_TYPE } from "uiCore/SbButton/SbButton";
import SbDivider from "uiCore/SbDivider/SbDivider";
import SbModal from "uiCore/SbModal/SbModal";
import SbSpin from "uiCore/SbSpin/SbSpin";
import SbEmpty from "uiCore/SbEmpty/SbEmpty";
import { getConsent } from "Api/apiService";
import { printDocument } from "Utils/printDocument";
import useAuthStore from "Store/useAuthStore";
import PlatformTerms from "Pages/Legal/PlatformTerms";
import LegalConsentModal from "Pages/Legal/LegalConsentModal";
import { TERMS_VERSION } from "constants/legal.constants";
import {
  PageWrapperStyled,
  ActionBarStyled,
  ComplianceButtonWrapperStyled,
  PreviewBarStyled,
  PageStatusStyled,
} from "Pages/Legal/Legal.styles";

const LegalCompliancePage = () => {
  const email = useAuthStore((s) => s.email);
  const [consent, setConsent] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [receiptOpen, setReceiptOpen] = useState(false);
  const [previewTerms, setPreviewTerms] = useState(false);

  const [loadError, setLoadError] = useState(false);

  const loadConsent = () => {
    setLoadError(false);
    return getConsent().then(setConsent).catch(() => setLoadError(true));
  };
  useEffect(() => { loadConsent(); }, []);

  // Re-consent is required when the accepted version is behind the current terms.
  const onCurrentVersion = consent?.terms_version === TERMS_VERSION;
  const hasConsent = !!consent?.consent_id && onCurrentVersion;
  const needsConsent = !!consent && (!consent.consent_id || !onCurrentVersion);
  const gated = needsConsent && !previewTerms;

  // Wait for consent before rendering, so disabled orgs don't see the page flash.
  if (!consent) {
    return (
      <PageStatusStyled vertical align="center" justify="center">
        {loadError ? (
          <SbEmpty description="Could not load your consent status.">
            <SbButton buttonType={BUTTON_TYPE.PRIMARY} onClick={loadConsent}>Retry</SbButton>
          </SbEmpty>
        ) : (
          <SbSpin size="large" />
        )}
      </PageStatusStyled>
    );
  }

  // Org not enabled for legal consent by the BE.
  if (!consent.enabled) return <Navigate to="/" replace />;

  return (
    <>
      <PageWrapperStyled $blurred={gated}>
        <ActionBarStyled justify="flex-end" align="center" gap={12} wrap>
          {hasConsent && (
            <>
              <ComplianceButtonWrapperStyled>
                <SbButton icon={<CheckCircleFilled />} onClick={() => setReceiptOpen(true)}>
                  Compliance Active
                </SbButton>
              </ComplianceButtonWrapperStyled>
              <SbButton buttonType={BUTTON_TYPE.PRIMARY} onClick={() => setEditOpen(true)}>
                Consent Settings
              </SbButton>
            </>
          )}
          <SbButton
            icon={<FilePdfOutlined />}
            onClick={() =>
              printDocument("legal-print-root", {
                footerLeft: "SkillsBridge.AI Inc. — Confidential & Proprietary Global Platform Documentation",
              })
            }
          >
            Save as PDF
          </SbButton>
        </ActionBarStyled>

        <div id="legal-print-root">
          <PlatformTerms />
        </div>

        <SbModal
          open={receiptOpen}
          title="Legal Consent Receipt"
          footer={null}
          onCancel={() => setReceiptOpen(false)}
        >
          <Alert
            type="success"
            showIcon
            message="A secure, cryptographically hashed compliance receipt has been logged on the SkillsBridge.AI enterprise registry."
          />
          <SbDivider />
          <Descriptions column={1} bordered size="small">
            <Descriptions.Item label="Consent ID">{consent?.consent_id}</Descriptions.Item>
            <Descriptions.Item label="Timestamp">{consent?.timestamp}</Descriptions.Item>
            <Descriptions.Item label="User Signature">{email}</Descriptions.Item>
            <Descriptions.Item label="IP Hash">{consent?.client_metadata?.ip_address_hash}</Descriptions.Item>
            <Descriptions.Item label="Research Telemetry">
              {consent?.research_opt_in ? "Opted-In (Granted)" : "Opted-Out (Withheld)"}
            </Descriptions.Item>
            <Descriptions.Item label="Status">Verifiable & Active</Descriptions.Item>
          </Descriptions>
        </SbModal>

        <LegalConsentModal
          open={editOpen}
          mode="edit"
          initial={consent}
          onViewTerms={() => setEditOpen(false)}
          onDone={() => { setEditOpen(false); loadConsent(); }}
          onCancel={() => setEditOpen(false)}
        />
      </PageWrapperStyled>

      {/* First-login consent gate — blocks until consent is recorded */}
      <LegalConsentModal
        open={gated}
        mode="create"
        onViewTerms={() => setPreviewTerms(true)}
        onDone={() => { setPreviewTerms(false); loadConsent(); }}
      />

      {/* Document preview mode — reading the terms during onboarding */}
      {needsConsent && previewTerms && (
        <PreviewBarStyled align="center" justify="center" gap="middle" wrap>
          <span>Viewing End-User Platform Terms &amp; Data Protection Notice (Document Preview Mode)</span>
          <SbButton buttonType={BUTTON_TYPE.PRIMARY} onClick={() => setPreviewTerms(false)}>
            Return to Consent
          </SbButton>
        </PreviewBarStyled>
      )}
    </>
  );
};

export default LegalCompliancePage;
